import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Briefcase, Users, DollarSign, ArrowUp, ArrowDown, History, Maximize2 } from "lucide-react";
import { fetchNfp } from "@/lib/api";

// NFP headline (thousands) → "+57K" / "+1.2M".
const fmtJobs = (v) => {
  if (v == null) return "—";
  const sign = v > 0 ? "+" : v < 0 ? "−" : "";
  const a = Math.abs(v);
  return a >= 1000 ? `${sign}${(a / 1000).toFixed(1)}M` : `${sign}${a}K`;
};
const fmtPct = (v) => (v == null ? "—" : `${Number(v).toFixed(1)}%`);

function fmtGst(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      weekday: "short", day: "2-digit", month: "short",
      hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "Asia/Dubai",
    });
  } catch {
    return "—";
  }
}

function pollInterval(nextRelease) {
  if (!nextRelease) return 1000 * 60 * 20;
  const r = new Date(nextRelease).getTime();
  const now = Date.now();
  return now >= r - 5 * 60 * 1000 && now <= r + 2 * 60 * 60 * 1000 ? 1000 * 60 : 1000 * 60 * 20;
}

function Verdict({ actual, forecast, kind = "good-high", pct = false }) {
  if (actual == null || forecast == null) return null;
  const diff = Number((actual - forecast).toFixed(pct ? 1 : 0));
  let label, cls;
  if (diff === 0) {
    label = "In-line";
    cls = "text-zinc-400";
  } else if (kind === "inflation") {
    label = diff > 0 ? "Hot" : "Soft";
    cls = diff > 0 ? "text-amber-term" : "text-zinc-400";
  } else {
    const good = kind === "good-low" ? diff < 0 : diff > 0;
    label = good ? "Beat" : "Miss";
    cls = good ? "text-pos" : "text-neg";
  }
  const mag = diff === 0 ? "" : ` ${diff > 0 ? "+" : "−"}${pct ? Math.abs(diff).toFixed(1) + "pp" : fmtJobs(Math.abs(diff)).replace("+", "")}`;
  return (
    <span className={`text-[9px] uppercase tracking-widest border border-current px-1 py-0.5 leading-none whitespace-nowrap ${cls}`}>
      {label}{mag}
    </span>
  );
}

function MetricRow({ Icon, label, actual, forecast, prev, big, pct = false, kind = "good-high", neutral = false, extra = null, testid }) {
  const bigCls = actual == null ? "text-zinc-500" : neutral ? "text-zinc-100" : actual >= 0 ? "text-pos" : "text-neg";
  return (
    <div className="px-3 py-2.5" data-testid={testid}>
      <div className="flex items-center gap-2">
        <Icon size={13} strokeWidth={1.5} className="text-amber-term shrink-0" />
        <span className="text-[11px] uppercase tracking-widest text-zinc-300 truncate">{label}</span>
        <span className={`ml-auto text-lg font-semibold tabular-nums leading-none ${bigCls}`} data-testid={`${testid}-actual`}>
          {big}
        </span>
      </div>
      <div className="flex items-center gap-2 mt-1.5 pl-[21px]">
        <span className="text-[10px] tabular-nums text-zinc-500" data-testid={`${testid}-cons`}>
          cons <span className="text-zinc-300">{pct ? fmtPct(forecast) : fmtJobs(forecast)}</span>
          <span className="mx-1 text-zinc-700">·</span>
          prev <span className="text-zinc-300">{pct ? fmtPct(prev) : fmtJobs(prev)}</span>
        </span>
        <span className="ml-auto">
          <Verdict actual={actual} forecast={forecast} kind={kind} pct={pct} />
        </span>
      </div>
      {extra && <div className="mt-1.5 pl-[21px]">{extra}</div>}
    </div>
  );
}

// 6-month payrolls trend — tiny bars around a zero baseline (positives up, negatives down).
function Sparkline({ data, revised }) {
  if (!data?.length) return null;
  const max = Math.max(...data.map((d) => Math.abs(d.value)), 1);
  const barH = (v) => Math.max(2, Math.round((Math.abs(v) / max) * 13));
  return (
    <div className="px-3 pt-2 pb-1.5 border-t border-zinc-900" data-testid="nfp-sparkline">
      <div className="text-[9px] uppercase tracking-widest text-zinc-600 mb-1">6-mo payrolls trend</div>
      <div className="flex items-stretch justify-between gap-1">
        {data.map((d, i) => {
          const pos = d.value >= 0;
          const latest = i === data.length - 1;
          const wasRevised = revised?.has(d.month);
          return (
            <div key={i} className="flex flex-col items-center flex-1 min-w-0" title={`${d.month}: ${fmtJobs(d.value)}${wasRevised ? " (revised)" : ""}`}>
              <div className="h-[6px] flex items-end">
                {wasRevised && <span className="w-1 h-1 rounded-full bg-amber-term" />}
              </div>
              <div className="w-full h-[14px] flex flex-col justify-end">
                {pos && <div className={`w-full rounded-t-[1px] text-pos bg-current ${latest ? "opacity-100 ring-1 ring-amber-term/60" : "opacity-70"}`} style={{ height: `${barH(d.value)}px` }} />}
              </div>
              <div className="w-full h-px bg-zinc-700" />
              <div className="w-full h-[14px] flex flex-col justify-start">
                {!pos && <div className={`w-full rounded-b-[1px] text-neg bg-current ${latest ? "opacity-100 ring-1 ring-amber-term/60" : "opacity-70"}`} style={{ height: `${barH(d.value)}px` }} />}
              </div>
              <span className={`text-[8px] uppercase tracking-wide mt-0.5 ${latest ? "text-amber-term font-medium" : "text-zinc-600"}`}>{d.month}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Wage growth y/y — tiny line chart so you can see acceleration/cooling.
function YoYLine({ data }) {
  if (!data?.length) return null;
  const vals = data.map((d) => d.value);
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const range = max - min || 1;
  const W = 100, H = 20, pad = 2.5;
  const xy = (v, i) => [
    pad + (i / Math.max(1, data.length - 1)) * (W - 2 * pad),
    pad + (1 - (v - min) / range) * (H - 2 * pad),
  ];
  const pts = vals.map((v, i) => xy(v, i).map((n) => n.toFixed(1)).join(",")).join(" ");
  const [lx, ly] = xy(vals[vals.length - 1], vals.length - 1);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="w-full h-5 text-amber-term" data-testid="nfp-wage-yoy-line">
      <polyline points={pts} fill="none" stroke="currentColor" strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
      <circle cx={lx} cy={ly} r="1.8" fill="currentColor" />
    </svg>
  );
}

function Trend({ cur, prev }) {
  if (cur == null || prev == null) return null;
  const diff = Number((cur - prev).toFixed(1));
  if (diff === 0) return <span className="text-[9px] uppercase tracking-widest text-zinc-500">flat</span>;
  const up = diff > 0;
  const Arrow = up ? ArrowUp : ArrowDown;
  return (
    <span className={`inline-flex items-center gap-0.5 text-[9px] uppercase tracking-widest ${up ? "text-amber-term" : "text-sky-400"}`}>
      <Arrow size={9} strokeWidth={2.5} />{up ? "accel" : "cooling"}
    </span>
  );
}

export default function NFPCard() {
  const navigate = useNavigate();
  const q = useQuery({
    queryKey: ["nfp"],
    queryFn: fetchNfp,
    staleTime: 1000 * 60 * 10,
    refetchInterval: (query) => pollInterval(query.state.data?.next_release),
    refetchOnWindowFocus: true,
  });
  const d = q.data;
  const revisedSet = d?.revisions?.length ? new Set(d.revisions.map((r) => r.month)) : null;

  return (
    <div data-testid="nfp-card" className="border border-zinc-800 bg-[#09090B]">
      <button
        onClick={() => navigate("/nfp")}
        data-testid="nfp-card-header"
        className="w-full px-3 py-2 border-b border-zinc-800 flex items-center justify-between gap-2 hover:bg-zinc-900/60 transition-colors group cursor-pointer"
      >
        <span className="text-[11px] uppercase tracking-widest text-zinc-400 flex items-center gap-1.5 whitespace-nowrap">
          <Briefcase size={11} strokeWidth={1.5} className="text-amber-term" /> NFP · US Labor
        </span>
        <span className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-widest text-zinc-600" data-testid="nfp-month">
            {d?.month || "—"}
          </span>
          <Maximize2 size={11} strokeWidth={1.5} className="text-zinc-600 group-hover:text-amber-term transition-colors" />
        </span>
      </button>

      {q.isLoading ? (
        <div className="h-[220px] flex items-center justify-center text-zinc-600 text-[11px] uppercase tracking-widest">Loading NFP…</div>
      ) : q.isError ? (
        <div className="h-[220px] flex items-center justify-center text-neg text-[11px] uppercase tracking-widest">NFP unavailable</div>
      ) : (
        <>
          <div className="divide-y divide-zinc-900">
            <MetricRow Icon={Briefcase} label="Payrolls" actual={d.nfp} forecast={d.nfp_forecast} prev={d.nfp_prev} big={fmtJobs(d.nfp)} kind="good-high" testid="nfp-payrolls-row" />
            <MetricRow Icon={Users} label="Unemployment" actual={d.unemployment} forecast={d.unemployment_forecast} prev={d.unemployment_prev} big={fmtPct(d.unemployment)} pct neutral kind="good-low" testid="nfp-unemployment-row" />
            <MetricRow
              Icon={DollarSign}
              label="Wages m/m"
              actual={d.ahe}
              forecast={d.ahe_forecast}
              prev={d.ahe_prev}
              big={fmtPct(d.ahe)}
              pct
              neutral
              kind="inflation"
              testid="nfp-wages-row"
              extra={
                d.ahe_yoy != null ? (
                  <div data-testid="nfp-wage-yoy">
                    <div className="flex items-center justify-between mb-0.5">
                      <span className="text-[9px] uppercase tracking-widest text-zinc-600">wage y/y</span>
                      <span className="flex items-center gap-1.5">
                        <span className="text-[10px] tabular-nums font-semibold text-zinc-200" data-testid="nfp-wage-yoy-value">{d.ahe_yoy.toFixed(1)}%</span>
                        <Trend cur={d.ahe_yoy} prev={d.ahe_yoy_prev} />
                      </span>
                    </div>
                    <YoYLine data={d.ahe_yoy_history} />
                  </div>
                ) : null
              }
            />
          </div>

          <Sparkline data={d.nfp_history} revised={revisedSet} />

          {d.revisions?.length > 0 && (
            <div className="px-3 py-1.5 border-t border-zinc-900" data-testid="nfp-revisions">
              <div className="flex items-center gap-1 text-[9px] uppercase tracking-widest text-zinc-600 mb-1">
                <History size={9} strokeWidth={1.5} /> Prior-month revisions
              </div>
              <div className="flex flex-col gap-0.5">
                {d.revisions.map((r, i) => (
                  <div key={i} className="flex items-center gap-1.5 text-[10px] tabular-nums" data-testid={`nfp-revision-${i}`}>
                    <span className="text-zinc-400 w-8 uppercase">{r.month}</span>
                    <span className="text-zinc-500">{fmtJobs(r.from)} → <span className="text-zinc-200">{fmtJobs(r.to)}</span></span>
                    <span className={`ml-auto inline-flex items-center gap-0.5 ${r.delta >= 0 ? "text-pos" : "text-neg"}`}>
                      {r.delta >= 0 ? <ArrowUp size={9} strokeWidth={2.5} /> : <ArrowDown size={9} strokeWidth={2.5} />}
                      {fmtJobs(Math.abs(r.delta)).replace("+", "")}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="px-3 py-1.5 border-t border-zinc-800 flex items-center justify-between text-[9px] uppercase tracking-widest text-zinc-600">
            <span>{d.source}</span>
            <span data-testid="nfp-next-release">Next · {fmtGst(d.next_release)} GST</span>
          </div>
        </>
      )}
    </div>
  );
}
